/**
 * Vacation Requests List Page
 * Route: /vacation/requests
 * Story: VAC-DS-03
 */

'use client'

import React, { useState } from 'react'
import { Plus, Home, Calendar, CheckCircle, AlertCircle } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import ProtectedRoute from '@/components/ProtectedRoute'
import { VacationRequestList } from '@/components/VacationRequestList'
import { VacationRequestDetailModal } from '@/components/VacationRequestDetailModal'
import { useVacationRequests, useCancelVacationRequest } from '@/hooks/useVacationRequests'
import type { VacationRequest } from '@/types/vacation'

function VacationRequestsPageContent() {
  const navigate = useNavigate()
  const { requests, isLoading, refetch } = useVacationRequests()
  const { cancelRequest } = useCancelVacationRequest()

  const [selectedRequest, setSelectedRequest] = useState<VacationRequest | null>(null)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleRequestClick = (request: VacationRequest) => {
    setSelectedRequest(request)
  }

  const handleCloseModal = () => {
    setSelectedRequest(null)
  }

  const handleCancelRequest = async (requestId: string) => {
    try {
      await cancelRequest(requestId)
      setSuccessMessage('Solicitação cancelada com sucesso')
      setErrorMessage('')
      refetch()

      // Auto-hide notification after 6 seconds
      setTimeout(() => setSuccessMessage(''), 6000)
    } catch (err) {
      setErrorMessage('Erro ao cancelar solicitação')
      setSuccessMessage('')

      // Auto-hide notification after 6 seconds
      setTimeout(() => setErrorMessage(''), 6000)
    }
  }

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <nav className="text-sm text-brand-gray mb-6" aria-label="breadcrumb">
          <a
            href="/dashboard"
            className="hover:text-white transition-colors inline-flex items-center"
          >
            <Home size={14} className="mr-1" />
            Dashboard
          </a>
          <span className="mx-2">›</span>
          <a
            href="/vacation"
            className="hover:text-white transition-colors inline-flex items-center"
          >
            <Calendar size={14} className="mr-1" />
            Férias
          </a>
          <span className="mx-2">›</span>
          <span className="text-white font-medium">Minhas Solicitações</span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-bold text-brand-primary">
              Minhas Solicitações de Férias
            </h1>
            <p className="text-brand-gray mt-1">
              {requests.length} solicitações encontradas
            </p>
          </div>
          <button
            onClick={() => navigate({ to: '/vacation/request' })}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus size={16} />
            <span>Nova Solicitação</span>
          </button>
        </div>

        {/* List */}
        <VacationRequestList
          requests={requests}
          isLoading={isLoading}
          onRequestClick={handleRequestClick}
          onCancelRequest={handleCancelRequest}
        />

        {/* Detail Modal */}
        <VacationRequestDetailModal
          request={selectedRequest}
          open={!!selectedRequest}
          onClose={handleCloseModal}
          onCancel={handleCancelRequest}
        />

        {/* Success Notification */}
        {successMessage && (
          <div className="fixed top-4 right-4 z-50 animate-fade-in">
            <div className="bg-success/10 border border-success/30 rounded-monumenta p-4 shadow-monumenta">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-success" size={20} />
                <p className="text-white font-display">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {errorMessage && (
          <div className="fixed top-4 right-4 z-50 animate-fade-in">
            <div className="bg-error/10 border border-error/30 rounded-monumenta p-4 shadow-monumenta">
              <div className="flex items-center space-x-3">
                <AlertCircle className="text-error" size={20} />
                <p className="text-white font-display">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function VacationRequestsPage() {
  return (
    <ProtectedRoute>
      <VacationRequestsPageContent />
    </ProtectedRoute>
  )
}
